/**
 * The web app is private (ADR 0021): without a signed-in account it shows only
 * this sign-in screen, never the workspace, so nobody can create notes in it.
 * The desktop app is not gated (it runs on your own computer), and test builds
 * can turn the gate off with VITE_LOCAL_ONLY=true. With allowLocal (config.json or
 * VITE_ALLOW_LOCAL=true) visitors may also continue without an account (their data stays in that browser).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isTauri } from '@nexus/store/tauri';
import { supabase } from './useSync';
import { server, serverConfigured } from './config';
import { ForgotPassword, MailLinkPassword } from './PasswordForms';
import { clearMailLink, linkExpired, mailLink } from './password';

const signInRequired = () =>
  serverConfigured() && !isTauri() && import.meta.env.VITE_LOCAL_ONLY !== 'true';

type AuthState = 'loading' | 'in' | 'out';

const LOCAL_KEY = 'nexus-open-local';

function chosenLocal(): boolean {
  try {
    return server().allowLocal && localStorage.getItem(LOCAL_KEY) === '1';
  } catch {
    return false;
  }
}

function useAuthState(): AuthState {
  const [state, setState] = useState<AuthState>('loading');
  useEffect(() => {
    // The stored session counts, also offline when its token can't be refreshed.
    const { data } = supabase().auth.onAuthStateChange((_event, session) =>
      setState(session ? 'in' : 'out'),
    );
    return () => data.subscription.unsubscribe();
  }, []);
  return state;
}

export function SignInGate({ children }: { children: ReactNode }) {
  if (!signInRequired()) return children;
  return <Gate>{children}</Gate>;
}

function Gate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const state = useAuthState();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgot, setForgot] = useState(false);
  const [local, setLocal] = useState(chosenLocal);
  const [link, setLink] = useState(mailLink);

  if (state === 'loading') return <p className="p-10 text-fg-muted">{t('app.loading')}</p>;
  // A mail link wins over a session already in this browser: its code signs in as the invitee.
  if (link)
    return (
      <main className="grid min-h-full place-items-center p-6">
        <div className="flex w-full max-w-sm flex-col gap-3 rounded-xl border border-border bg-bg-subtle p-6 shadow-sm">
          <MailLinkPassword
            link={link}
            onDone={() => {
              clearMailLink();
              setLink(null);
            }}
            onExpired={() => {
              clearMailLink();
              setLink(null);
              setForgot(true);
            }}
          />
        </div>
      </main>
    );
  if (state === 'in' || local) return children;

  const field = 'rounded-lg border border-border bg-bg px-2 py-1.5 text-sm';
  if (forgot)
    return (
      <main className="grid min-h-full place-items-center p-6">
        <div className="flex w-full max-w-sm flex-col gap-3 rounded-xl border border-border bg-bg-subtle p-6 shadow-sm">
          <h1 className="text-lg font-semibold">{t('password.forgotTitle')}</h1>
          <ForgotPassword initialEmail={email} onBack={() => setForgot(false)} />
        </div>
      </main>
    );
  return (
    <main className="grid min-h-full place-items-center p-6">
      <form
        aria-labelledby="gate-title"
        className="flex w-full max-w-sm flex-col gap-3 rounded-xl border border-border bg-bg-subtle p-6 shadow-sm"
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          void supabase()
            .auth.signInWithPassword({ email: email.trim(), password })
            .then(({ error: err }) => {
              if (err) setError(navigator.onLine ? t('gate.failed') : t('gate.offline'));
            })
            .finally(() => {
              setBusy(false);
              setPassword('');
            });
        }}
      >
        <div className="flex items-center gap-2">
          <img src="/icon.svg" alt="" className="size-7" />
          <h1 id="gate-title" className="text-lg font-semibold">
            Nexus Open
          </h1>
        </div>
        <p className="text-sm text-fg-muted">{t('gate.intro')}</p>
        {linkExpired && (
          <p role="alert" className="text-sm text-danger">
            {t('password.linkExpired')}
          </p>
        )}
        <label className="flex flex-col gap-1 text-sm">
          {t('sync.email')}
          <input
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={field}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          {t('gate.password')}
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={field}
          />
        </label>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="rounded-full bg-accent px-3 py-2 text-sm font-medium text-accent-fg disabled:opacity-50"
        >
          {t('sync.signIn')}
        </button>
        <button
          type="button"
          onClick={() => setForgot(true)}
          className="self-start text-xs text-fg-muted underline hover:text-fg"
        >
          {t('password.forgot')}
        </button>
        {server().allowLocal && (
          <div className="mt-2 border-t border-border pt-3">
            <button
              type="button"
              onClick={() => {
                try {
                  localStorage.setItem(LOCAL_KEY, '1');
                } catch {
                  // Private window: only for this visit.
                }
                setLocal(true);
              }}
              className="text-sm font-medium text-accent hover:underline"
            >
              {t('sync.continueLocal')}
            </button>
            <p className="text-xs text-fg-muted">{t('sync.continueLocalHint')}</p>
          </div>
        )}
      </form>
    </main>
  );
}
